import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaThumbsUp, FaComments, FaTag, FaCrown, FaShieldAlt, FaChartLine, FaHeart, FaFileAlt, FaArrowRight, FaStar } from 'react-icons/fa';
import HeaderLandingPage from '../components/HeaderLandingPage';

const CommunityForumInfo = () => {
  // Initialize dark mode from localStorage or default to false
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('cashsule-theme');
    return savedTheme === 'dark';
  });
  
  // Toggle dark mode and save to localStorage
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('cashsule-theme', newDarkMode ? 'dark' : 'light');
  };
  
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    // Set up dark mode for this component only
    const pageContainer = document.getElementById('forum-info-page-container');
    
    if (isDarkMode) {
      pageContainer.classList.add('dark');
    } else {
      pageContainer.classList.remove('dark');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark');
    };
  }, [isDarkMode]);

  const features = [
    {
      icon: <FaComments className="w-6 h-6" />,
      title: "Create & Share Posts",
      description: "Share your financial journey, ask questions, and help others with their money management goals.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <FaThumbsUp className="w-6 h-6" />,
      title: "Community Voting",
      description: "Upvote helpful posts and downvote irrelevant content to surface the best financial advice.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: <FaHeart className="w-6 h-6" />,
      title: "Follow Discussions",
      description: "Keep track of interesting conversations and get notified when there are new replies.",
      gradient: "from-pink-500 to-rose-500"
    },
    {
      icon: <FaTag className="w-6 h-6" />,
      title: "Organised Categories",
      description: "Find content easily with labels like 'Budgeting', 'Investing', 'Debt Management', and more.",
      gradient: "from-purple-500 to-indigo-500"
    },
    {
      icon: <FaChartLine className="w-6 h-6" />,
      title: "Real-time Stats",
      description: "See community activity with live statistics on posts, comments, and active members.",
      gradient: "from-orange-500 to-amber-500"
    },
    {
      icon: <FaFileAlt className="w-6 h-6" />,
      title: "Comprehensive Discussions",
      description: "Engage in detailed conversations with a full comment system and threaded replies.",
      gradient: "from-teal-500 to-cyan-500"
    }
  ];

  return (
    <div id="forum-info-page-container" className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <HeaderLandingPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      {/* Content starts after the header with padding */}
      <div className="pt-16">
        {/* Breadcrumb Navigation */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4">
          <div className="flex items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <Link
              to="/"
              className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">Community Forum</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-1 sm:pb-2">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <div className="flex justify-center items-center mb-3 sm:mb-4 lg:mb-6">
              <div className="p-2 sm:p-3 lg:p-4 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full text-white shadow-lg">
                <FaUsers className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />
              </div>
              <div className="ml-2 sm:ml-3 lg:ml-4 p-1 sm:p-1.5 lg:p-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-white shadow-lg">
                <FaCrown className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 lg:mb-6 px-2">
              Community Forum
              <span className="block text-lg sm:text-xl lg:text-2xl xl:text-3xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-500 mt-1 sm:mt-2">
                Premium Feature
              </span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-4 sm:mb-6 lg:mb-8 px-4">
              Connect with like-minded individuals, share financial wisdom, and grow your money management skills in our exclusive premium community.
            </p>
          </div>
        </section>

        {/* Why Premium Section */}
        <section className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-8 sm:py-12 lg:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-8 lg:mb-12">
              <div className="flex justify-center mb-3 sm:mb-4 lg:mb-6">
                <div className="p-2 sm:p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-white">
                  <FaShieldAlt className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
                </div>
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4 px-2">
                Why Is Our Forum Premium?
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto px-4">
                We believe in maintaining a high-quality, supportive community environment without requiring extensive manual moderation.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              <div className="bg-white dark:bg-gray-600 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-600 dark:text-green-400 mr-2 sm:mr-3">
                    <FaStar className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Quality Control</h3>
                </div>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                  Premium access ensures committed users who are serious about their financial growth, leading to higher quality discussions and advice.
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-600 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400 mr-2 sm:mr-3">
                    <FaShieldAlt className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Safe Environment</h3>
                </div>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                  Reduced spam, trolling, and irrelevant content creates a safer space for sharing personal financial experiences and goals.
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-600 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400 mr-2 sm:mr-3">
                    <FaUsers className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Focused Community</h3>
                </div>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                  A smaller, dedicated community means more meaningful connections and personalised financial advice from people who understand your journey.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4 px-2">
                Powerful Forum Features
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto px-4">
                Everything you need to connect, learn, and grow your financial knowledge in one place.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${feature.gradient}`}></div>
                  <div className="p-4 sm:p-5 lg:p-6">
                    <div className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg bg-gradient-to-r ${feature.gradient} text-white mb-3 sm:mb-4`}>
                      {React.cloneElement(feature.icon, { className: "w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" })}
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshot Preview Section */}
        <section className="bg-gray-100 dark:bg-gray-800 py-8 sm:py-10 lg:py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              {/* Left Content */}
              <div className="order-2 lg:order-1">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                  See the Forum in Action
                </h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 mb-4 sm:mb-6">
                  Get a glimpse of what our premium community looks like. Experience real discussions, 
                  community voting, and organised categories that help you find exactly what you need.
                </p>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-500 rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                    Interactive posts with upvoting and comments
                  </div>
                  <div className="flex items-center text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                    Organised categories and real-time statistics
                  </div>
                  <div className="flex items-center text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                    Clean, professional interface designed for engagement
                  </div>
                </div>
              </div>
              
              {/* Right Screenshot */}
              <div className="order-1 lg:order-2">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden">
                  <img
                    src="/cashsuleforumdemo.png"
                    alt="Cashsule Community Forum Preview"
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-8 sm:py-10 lg:py-12 xl:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-8 lg:mb-12 xl:mb-16">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4">
                How the Forum Works
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 px-4">
                Simple steps to start engaging with our financial community
              </p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base lg:text-lg xl:text-xl mx-auto mb-2 sm:mb-3 lg:mb-4">
                  1
                </div>
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2 px-1 sm:px-2">Sign Up for Premium</h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300 px-1 sm:px-2">Get premium access to unlock the community forum feature</p>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base lg:text-lg xl:text-xl mx-auto mb-2 sm:mb-3 lg:mb-4">
                  2
                </div>
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2 px-1 sm:px-2">Accept Community Guidelines</h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300 px-1 sm:px-2">Agree to our community standards to maintain a positive environment</p>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base lg:text-lg xl:text-xl mx-auto mb-2 sm:mb-3 lg:mb-4">
                  3
                </div>
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2 px-1 sm:px-2">Create Your First Post</h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300 px-1 sm:px-2">Share your financial goals, questions, or tips with the community</p>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base lg:text-lg xl:text-xl mx-auto mb-2 sm:mb-3 lg:mb-4">
                  4
                </div>
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2 px-1 sm:px-2">Engage & Learn</h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300 px-1 sm:px-2">Vote on posts, follow discussions, and learn from others' experiences</p>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home Section like Terms.jsx */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="text-center">
            <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm">
              <Link 
                to="/" 
                className="text-primary-600 dark:text-primary-400 hover:underline ml-2"
              >
                Back to Home
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityForumInfo; 